import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { isEmpty } from 'lodash';
import { RESET, SYSTEM_COLLAPSE, USER_EXPAND } from 'common/actions/row-groups/types';
import { EBITDA_MARGIN_ALIAS, PERFORMANCE_METRICS_TITLE } from 'common/constants/financials';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { FinancialStatementPeriod } from 'common/types/financials/financials';
import { USD_THOUSANDS } from 'components/FeaturedSpreadsheet/constants';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { useFinancials } from 'context/FinancialsContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { rowConfig, template } from 'pages/Financials/performance-metrics/data';
import { parser, parser as pFParser } from 'pages/Financials/performance-metrics/utilities';
import trimBlankIncomeStatementColumns from 'pages/Financials/utilities/trimBlankIncomeStatementColumns';
import UsePerformanceMetricsDataProps from './usePerformanceMetricsData.props';
import { KpiConfigurationRow } from '../kpi/config/types';

const usePerformanceMetricsData = ({
  periods,
  fiscalYearData,
  isDisabled,
  incomeStatementSheet,
  isExportMode = false,
}: UsePerformanceMetricsDataProps) => {
  const { useAdjustedEBITDA } = useFinancials();
  const [columns, setColumns] = useState<FinancialStatementPeriod[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<FinancialStatementPeriod[]>([]);
  const [cells, setCells] = useState<ICell[]>([]);

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, {
    [EBITDA_MARGIN_ALIAS]: useAdjustedEBITDA ? USER_EXPAND : SYSTEM_COLLAPSE,
  });

  useEffect(() => {
    setRowGroups({
      type: RESET,
      payload: { [EBITDA_MARGIN_ALIAS]: useAdjustedEBITDA ? USER_EXPAND : SYSTEM_COLLAPSE },
    });
  }, [useAdjustedEBITDA]);

  const config = useMemo(() => rowConfig() as KpiConfigurationRow[], []);

  const customTitles = useMemo(() => {
    const newConfig = [...config];

    // Reset First Row
    newConfig[0] = {
      ...newConfig[0],
      rowSpan: 3,
      value: PERFORMANCE_METRICS_TITLE,
      className: 'spreadsheet-table-title',
    };

    return newConfig;
  }, [config]);

  const updateCells = useCallback(newCells => {
    setCells(newCells);
  }, []);

  const tableTerms = useMemo(
    () => ({
      tableName: PERFORMANCE_METRICS_TITLE,
      tableSlug: 'financials-performance-metrics',
      columnName: 'Performance Metric',
      pluralColumnName: 'Performance Metrics',
    }),
    []
  );

  useEffect(() => {
    if (columns?.length) {
      const newVisibleColumns = columns.filter(column => !column.is_deleted);
      // Performance metrics mirror income statement cells by column legend
      // (`=incomeStatement.<legend><row>`), so the two sheets have to hide the
      // same blank projection years or every mirrored cell shifts one column over.
      setVisibleColumns(isExportMode ? trimBlankIncomeStatementColumns(newVisibleColumns) : newVisibleColumns);
    }
  }, [columns, isExportMode]);

  useEffect(() => {
    if (periods) {
      const generatedColumns: FinancialStatementPeriod[] = [];

      periods.forEach(column => {
        generatedColumns.push({
          ...column,
          ...template,
        });
      });

      setColumns(generatedColumns);
    }
  }, [periods]);

  const applyParser = useCallback(async () => {
    try {
      const parsedCells = await parser({
        columns,
        tableData: {
          incomeStatementSheet,
        },
        rowConfig: config,
      });

      updateCells(parsedCells);
    } catch (error) {
      throw new Error(error as string);
    }
  }, [columns, config, incomeStatementSheet, updateCells]);

  const customParser = useCallback(params => pFParser({ ...params, fiscalYearData }), [fiscalYearData]);

  useEffect(() => {
    if (!isEmpty(incomeStatementSheet?.cells)) {
      applyParser();
    }
  }, [applyParser, incomeStatementSheet]);

  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetConfig | null>(null);

  useEffect(() => {
    if (visibleColumns) {
      setSpreadsheet(
        new SpreadsheetConfig({
          columns: visibleColumns,
          parser: customParser,
          rowConfig: config,
          name: 'performanceMetrics',
          tableData: { incomeStatementSheet, columns: visibleColumns },
          setColumns: setColumns as any,
          customTitles: customTitles as any,
          tableTerms: tableTerms as any,
          currency: USD_THOUSANDS,
          showTotalColumn: false,
          showToolbar: true,
          showPreviousColsDivider: true,
          allowConfirmAndDeleteColumn: !isDisabled,
          hasColTitle: false,
          allowDeleteColumn: !isDisabled,
          allowCopyColumn: !isDisabled,
        } as any)
      );
    }
  }, [config, customParser, customTitles, incomeStatementSheet, tableTerms, visibleColumns, isDisabled]);

  return {
    tableTerms,
    customTitles,
    cells,
    visibleColumns,
    config,
    updateCells,
    setColumns,
    spreadsheet,
    rowGroups,
    setRowGroups,
  };
};

export default usePerformanceMetricsData;
